import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { CommentAnchor } from '@peruselab/api-client/discussions';
import type { DrawingVisibility } from '@peruselab/api-client/moleculeDrawings';
import { elementSymbol } from '@peruselab/structure-editor/chemistry/elements';
import type { DrawingAtom } from '@peruselab/structure-editor/domain/drawingDocumentProjections';
import type { EditorSelection } from '@peruselab/structure-editor/domain/editor';
import type { StructureCorrespondence } from '@peruselab/structure-editor/domain/structureIdRemap';
import { moleculePagePath } from '../public/publicAccess';
import { defaultAnchorLabel } from './CommentItem';
import { DiscussionThread } from './DiscussionThread';
import { anchorToSelection, selectionToAnchor } from './selectionAnchor';

/** Names shown in an anchor label before "and N more". */
const LISTED = 4;

export interface WorkspaceDiscussionDrawing {
  readonly id: string;
  readonly moleculeId: string | null;
  readonly correspondence: StructureCorrespondence | null;
  readonly visibility: DrawingVisibility;
}

interface WorkspaceDiscussionPanelProps {
  /** The open drawing as last saved; null until it is saved. */
  readonly drawing: WorkspaceDiscussionDrawing | null;
  /** The atoms and bonds on the paper are the saved Molecule's (see useDrawingPersistence). */
  readonly structureMatchesSaved: boolean;
  readonly selection: EditorSelection;
  readonly atoms: readonly DrawingAtom[];
  readonly onSelect: (selection: EditorSelection) => void;
  readonly onDrawingCount: (count: number) => void;
  readonly onMoleculeCount: (count: number) => void;
}

/**
 * ADR 0016 phase 1 part C: the workspace's Discuss tab -- the open drawing's discussion and its
 * Molecule's. Selected atoms or bonds become the new comment's anchor ("About: C a1, O a3"), and
 * tapping an anchored comment's label selects what it is about on the paper (owner, 2026-10-09).
 * Anchors are in the saved Molecule's IDs, so they're offered only while the atoms and bonds on
 * the paper are the saved ones.
 */
export function WorkspaceDiscussionPanel({
  drawing,
  structureMatchesSaved,
  selection,
  atoms,
  onSelect,
  onDrawingCount,
  onMoleculeCount
}: WorkspaceDiscussionPanelProps) {
  const { t } = useTranslation();
  const correspondence = drawing?.correspondence ?? null;
  const hasSelection = selection.atomIds.length > 0 || selection.bondIds.length > 0;

  const selectionAnchor = useMemo(
    () => (drawing && structureMatchesSaved ? selectionToAnchor(selection, correspondence) : null),
    [drawing, structureMatchesSaved, selection, correspondence]
  );

  const atomsById = useMemo(() => new Map(atoms.map((atom) => [atom.id, atom])), [atoms]);
  const describeAnchor = useCallback(
    (anchor: CommentAnchor) => {
      const editorIds = anchorToSelection(anchor, correspondence);
      // Not about the structure (a property, a factsheet statement): the shared wording.
      if (!editorIds) return defaultAnchorLabel(anchor, t);
      const names = [
        ...editorIds.atomIds.map((id) => {
          const atom = atomsById.get(id);
          return atom ? `${elementSymbol(atom.atomicNumber)} ${id}` : id;
        }),
        ...editorIds.bondIds.map((id) => t('discussions.anchor.bondName', { id }))
      ];
      const items =
        names.length > LISTED
          ? t('discussions.anchor.andMore', { list: names.slice(0, LISTED).join(', '), count: names.length - LISTED })
          : names.join(', ');
      return t('discussions.anchor.about', { items });
    },
    [atomsById, correspondence, t]
  );

  const showAnchor = useCallback(
    (anchor: CommentAnchor) => {
      // #910: a factsheet statement lives on the molecule's page -- open it there, in a new tab,
      // so the paper stays as it is (owner, 2026-10-10).
      if (anchor.kind === 'statement' || anchor.kind === 'assertion') {
        if (drawing?.moleculeId) {
          const target = anchor.kind === 'statement' ? anchor.statementKey : `a-${anchor.assertionId}`;
          window.open(`${moleculePagePath(drawing.moleculeId, true)}#${target}`, '_blank', 'noopener');
        }
        return;
      }
      const editorIds = anchorToSelection(anchor, correspondence);
      if (!editorIds) return;
      // Only what is still on the paper; the rest was edited away since the comment.
      const atomIds = editorIds.atomIds.filter((id) => atomsById.has(id));
      onSelect({ atomIds, bondIds: editorIds.bondIds, annotationIds: [] });
    },
    [atomsById, correspondence, drawing, onSelect]
  );

  if (!drawing) {
    return (
      <div className="workspace-discussion">
        <p className="discussion-note">{t('discussions.workspace.saveFirst')}</p>
      </div>
    );
  }

  return (
    <div className="workspace-discussion">
      {hasSelection ? (
        <p className="discussion-note">
          {structureMatchesSaved ? t('discussions.workspace.anchorHint') : t('discussions.workspace.saveToAnchor')}
        </p>
      ) : (
        <p className="discussion-note">{t('discussions.workspace.selectToAnchor')}</p>
      )}
      <DiscussionThread
        kind="molecule-drawing"
        artifactId={drawing.id}
        label={t('discussions.workspace.drawing')}
        selectionAnchor={selectionAnchor}
        describeAnchor={describeAnchor}
        onShowAnchor={showAnchor}
        onCountChange={onDrawingCount}
      />
      {drawing.moleculeId ? (
        <DiscussionThread
          kind="molecule"
          artifactId={drawing.moleculeId}
          label={t('discussions.workspace.molecule')}
          labelHref={moleculePagePath(drawing.moleculeId, true)}
          hostPrivate={drawing.visibility === 'private'}
          selectionAnchor={selectionAnchor}
          describeAnchor={describeAnchor}
          onShowAnchor={showAnchor}
          onCountChange={onMoleculeCount}
        />
      ) : null}
    </div>
  );
}
